// src/Containers/Repertoire/RepertoirePlay.tsx

import { Box, MenuItem, TextField } from '@mui/material';
import RepertoirePlayContainer from './RepertoirePlayContainer';
import RepertoirePlayContainerOSMD from './RepertoirePlayContainerOSMD';
import { useOSMDPhaserBackend } from 'Utils/FeatureFlags';
import RepBackArrow from 'assets/images/Repertoire/RepBackArrow.png';
import TempoIconLight from 'assets/images/Repertoire/TempoIconLight.svg';
import AccuracyMusicIcon from 'assets/images/Repertoire/AccuracyMusicIcon.png';
import PerformanceIcon from 'assets/images/Repertoire/PerformanceIcon.png';
import TempoPlus from 'assets/images/Repertoire/TempoPlus.png';
import TempoMinus from 'assets/images/Repertoire/TempoMinus.png';
import HandActive from 'assets/images/Repertoire/HandActive.png';
import HandInactive from 'assets/images/Repertoire/HandInactive.png';
import Reset from 'assets/images/Repertoire/Reset.svg';
import Restart from 'assets/images/Repertoire/Restart.png';
import RestartBig from 'assets/images/Repertoire/RestartBig.png';
import Play from 'assets/images/Play.png';
import Pause from 'assets/images/Pause.png';
import SettingsIcon from 'assets/images/Repertoire/SettingsIcon.png';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';
import { MainAppReducer } from 'Types';
import { RepertoireReducer, UserRepertoireProgress } from 'Types/RepertoireTypes';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useHoldToRepeat } from 'Hooks/useHoldToRepeat';
import { useRepertoirePlayContext } from 'Contexts/RepertoirePlayContext';
import Fullscreen from 'Components/Fullscreen';
import { find } from 'lodash';
import { CustomSwitch } from 'Components';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { getNamesConcatted } from 'Utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { isCapacitorNative } from 'Utils/Constants';
import { useTheaterUI } from './Utils';
import AppTour, { AppTourStep, TooltipRelativePosition } from 'Components/AppTour';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';

interface StyledSliderProps {
  handleSliderChange: (event: Event, value: number | number[]) => void;
  performanceTempo: number;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  tempPctAdjustment: number;
  setTempPctAdjustment: (value: number) => void;
}

const MAX_TEMPO_PCT = 3.0; // 300%
const MIN_TEMPO_PCT = 0.1; // 10%
const MAX_TRANSPOSE_SEMITONES = 7;
const TRANSPOSE_SEMITONE_OPTIONS = Array.from(
  { length: MAX_TRANSPOSE_SEMITONES * 2 + 1 },
  (_, index) => index - MAX_TRANSPOSE_SEMITONES,
);
const MODE_CONVERSION_OPTIONS: Array<{ value: ParallelModeConversion; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'to_minor', label: 'To Minor (Parallel)' },
  { value: 'to_major', label: 'To Major (Parallel)' },
];

const getTransposeLabel = (semitoneSteps: number): string => {
  if (semitoneSteps === 0) {
    return 'Concert (0)';
  }
  const absSemitones = Math.abs(semitoneSteps);
  return `${semitoneSteps > 0 ? '+' : ''}${semitoneSteps} ${
    absSemitones === 1 ? 'semitone' : 'semitones'
  }`;
};

const repertoireSettingsSelectSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    color: '#ECECED',
    backgroundColor: '#243056',
    borderRadius: '12px',
    fontWeight: 600,
    fontSize: '14px',
    minHeight: '42px',
  },
  '& .MuiSelect-select': {
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#525969',
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiSvgIcon-root': {
    color: '#ECECED',
    fontSize: '1.35rem',
  },
};

const repertoireSettingsSelectMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#0D172A',
      border: '1px solid #3E4C73',
      borderRadius: '12px',
      boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
      marginTop: '6px',
      '& .MuiMenuItem-root': {
        color: '#ECECED',
        fontSize: '14px',
        minHeight: '36px',
      },
      '& .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(111, 135, 241, 0.16)',
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(111, 135, 241, 0.24)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: 'rgba(111, 135, 241, 0.30)',
      },
    },
  },
};

const StyledSlider: React.FC<StyledSliderProps> = ({
  handleSliderChange,
  performanceTempo: _performanceTempo,
  sliderValue,
  setSliderValue: _setSliderValue,
  tempPctAdjustment,
  setTempPctAdjustment: _setTempPctAdjustment,
}) => {
  // This maps for the sliding scale to be 10%-300% but middle still be 100%
  const sliderStyle = {
    color: '#6F87F1',
    '& .MuiSlider-track': {
      color: '#2D3A67',
    },
    '& .MuiSlider-rail': {
      color: '#2D3A67',
    },
  };
  return (
    <Box sx={{ textAlign: 'center', margin: '20px 30px 10px 30px' }}>
      <Slider
        sx={sliderStyle}
        value={sliderValue}
        onChange={handleSliderChange}
        min={0}
        max={100}
        aria-labelledby="continuous-slider"
        valueLabelFormat={(_x) => `${Math.round(sliderValue)}%`}
      />
      <Typography>{`${Math.round(tempPctAdjustment)}%`}</Typography>
    </Box>
  );
};

interface RepertoirePlayProps {
  setRenderRepertoirePlay: (value: boolean) => void;
}

const RepertoirePlay = (props: RepertoirePlayProps) => {
  const { setRenderRepertoirePlay: _setRenderRepertoirePlay } = props;
  const dispatch = useDispatch();
  const location = useLocation();

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const repertoireData = repertoireReducerData.repertoireData;
  const repertoireIdSelect = repertoireReducerData.repertoireIdSelect;
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const { repertoireEnded } = repertoireReducerData;
  const repertoireProgressSelected = useMemo(
    () => find(userRepertoireProgress, (urd) => urd?.repertoire?.id === repertoireIdSelect),
    [userRepertoireProgress, repertoireIdSelect],
  );
  const [tempo, setTempo] = useState(0);
  const repertoireSelected = find(repertoireData, (rd) => rd.id === repertoireIdSelect);
  const isOSMDMode = useOSMDPhaserBackend();

  console.error(
    '[MUS-145-DBG] RepertoirePlay render — repertoireIdSelect:',
    repertoireIdSelect,
    'isOSMDMode:',
    isOSMDMode,
    'repertoireEnded:',
    repertoireEnded,
    'repertoireSelected:',
    repertoireSelected?.name ?? 'none',
  );

  const _levelSelect = data.levelSelect;
  const [sliderValue, setSliderValue] = useState(50);
  const [tempPctAdjustment, setTempPctAdjustment] = useState(100);
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [modeConversion, setModeConversion] = useState<ParallelModeConversion>('off');
  const [showDetails, setShowDetails] = useState(false);
  const [showAccuracyDetails, setShowAccuracyDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAccuracy, setShowAccuracy] = useState(false);
  const [cursorIsVisible, setCursorIsVisible] = useState(true);
  const [manualTempoInput, setManualTempoInput] = useState(tempo.toString());
  const tempoInputRef = useRef<HTMLInputElement>(null);
  const [hasLoadedTempo, setHasLoadedTempo] = useState(false);
  const [repTourOpen, setRepTourOpen] = useState(false);
  const {
    accuracyPercent,
    setAccuracy,
    showCursorSelector,
    setShowCursorSelector,
    performanceTempo,
    inPerformanceMode,
    setInPerformanceMode,
    softRefreshRepertoire,
  } = useRepertoirePlayContext();
  const max_tempo_value = performanceTempo != null ? performanceTempo * MAX_TEMPO_PCT : 0;
  const min_tempo_value = performanceTempo != null ? performanceTempo * MIN_TEMPO_PCT : 9999;

  const { userData } = data;
  // This block is required if a user is at end of repertoire then reloads the page manually, navigates back with buttons or browser, we want to reset this.
  useEffect(() => {
    dispatch(repertoireActions.setRepertoireEnded(false));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (!inPerformanceMode || native) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientY < 50) {
        setHeaderVisible(true);
      } else if (e.clientY > 120) {
        setHeaderVisible(false);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [inPerformanceMode]);

  useEffect(() => {
    if (performanceTempo && tempPctAdjustment) {
      setTempo(Math.round((performanceTempo * tempPctAdjustment) / 100.0));
    }
  }, [performanceTempo, tempPctAdjustment]);

  useEffect(() => {
    if (repertoireProgressSelected?.last_tempo && !hasLoadedTempo) {
      setHasLoadedTempo(true);
      mapTempoToSlider(repertoireProgressSelected?.last_tempo);
    }
  }, [repertoireProgressSelected?.last_tempo]);

  // const softRefreshRepertoire = useCallback(() => {
  //   // To "refresh" the page, we render this whole component false and then true again to remount everything.
  //   setRenderRepertoirePlay(false)
  //   // setTimeout(() => {
  //   //   setRenderRepertoirePlay(true)
  //   // }, 250)
  // }, [repertoireEnded])

  const [scrollPosition, setScrollPosition] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Wrapper to scroll to top and then soft refresh
  const handleSoftRefresh = useCallback(async () => {
    // Scroll to top immediately
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    await softRefreshRepertoire();
  }, [softRefreshRepertoire]);

  const handleTransposeChange = useCallback(
    (nextSemitones: number) => {
      if (nextSemitones === transposeSemitones) {
        return;
      }
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
      dispatch(repertoireActions.setRepertoireEnded(false));
      setTransposeSemitones(nextSemitones);
    },
    [dispatch, transposeSemitones],
  );

  const handleModeConversionChange = useCallback(
    (nextMode: ParallelModeConversion) => {
      if (nextMode === modeConversion) {
        return;
      }
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
      dispatch(repertoireActions.setRepertoireEnded(false));
      setModeConversion(nextMode);
    },
    [dispatch, modeConversion],
  );

  const playLevel = useCallback(() => {
    dispatch(appActions.setPlayOnStart(false));
    console.log('playing level', repertoireEnded);
    if (repertoireEnded) {
      // setRenderRepertoirePlay(false)
      setTimeout(async () => {
        // setRenderRepertoirePlay(true)
        // IMPORTANT: await handleSoftRefresh to ensure scheduler is fully reset
        // before starting playback (also scrolls to top)
        await handleSoftRefresh();
        dispatch(repertoireActions.setRepertoireEnded(false));
        dispatch(appActions.setPlayOnStart(true));
        console.log('setting repertoire playing to true', !repertoireReducerData.repertoirePlaying);
        dispatch(
          repertoireActions.setRepertoirePlaying({
            repertoirePlaying: !repertoireReducerData.repertoirePlaying,
          }),
        );
      }, 250);
    } else {
      dispatch(
        repertoireActions.setRepertoirePlaying({
          repertoirePlaying: !repertoireReducerData.repertoirePlaying,
        }),
      );
    }
  }, [dispatch, repertoireReducerData.repertoirePlaying, repertoireEnded, handleSoftRefresh]);

  const lastSpacePressRef = useRef(0);
  // Need to prevent space (play) from scrolling down lol.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        event.preventDefault();
        const now = Date.now();
        if (!repertoireEnded && now - lastSpacePressRef.current < 300) {
          // Double-tap detected
          handleSoftRefresh();
        } else {
          playLevel();
        }
        lastSpacePressRef.current = now;
      } else if (event.key === 'r' || event.key === 'R') {
        handleSoftRefresh();
      } else if (event.key === 'Enter') {
        if (tempoInputRef.current) {
          tempoInputRef.current.blur();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [repertoireReducerData.repertoirePlaying, repertoireEnded, handleSoftRefresh]);

  const setManualTempoChange = () => {
    let value = parseInt(manualTempoInput, 10);
    if (!isNaN(value)) {
      if (value > max_tempo_value) {
        value = max_tempo_value;
      } else if (value < min_tempo_value) {
        value = min_tempo_value;
      }
      mapTempoToSlider(value);
    }
    setManualTempoInput(tempo.toString());
  };

  const mapTempoToSlider = (newTempo: number) => {
    if (performanceTempo != null) {
      const newTempPctAdjustment = (100 * newTempo) / performanceTempo; // DO not round yet otherwise will not trigger change in tempo (since based on percentage). Only round in display.
      let newValue = ((newTempPctAdjustment - 100) * (100 / 2)) / (2 * 100) + 100 / 2;
      if (newValue <= 50) {
        newValue = ((newTempPctAdjustment - 10) / 90) * (100 / 2);
      }
      setSliderValue(newValue);
      setTempPctAdjustment(newTempPctAdjustment);
    }
  };

  const native = isCapacitorNative();
  const {
    headerVisible,
    setHeaderVisible,
    showHeaderTemporarily: _showHeaderTemporarily,
    hideHeaderSoon,
  } = useTheaterUI({
    isNative: native,
    inPerformanceMode,
    containerRef,
  });
  const togglePerformanceMode = useCallback(() => {
    setInPerformanceMode((prev: boolean) => {
      const next = !prev;
      setHeaderVisible(!next ? true : false);
      return next;
    });
  }, [setInPerformanceMode, setHeaderVisible]);

  useEffect(() => {
    if (containerRef.current && scrollPosition != null) {
      // setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      }
      // }, 2000)
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (!userData?.repertoire_tutorial_seenat) {
      setTimeout(() => {
        setRepTourOpen(true);
      }, 500);
    }
  }, []);

  if (!repertoireSelected) {
    return <></>;
  }

  const RepertoireTourSteps: AppTourStep[] = [
    {
      targetId: 'rep-play-button',
      title: 'Play / Pause',
      content: 'Tap to start or pause playback. Double-press space to restart.',
      position: TooltipRelativePosition.LEFT,
    },
    {
      targetId: 'rep-performance-mode',
      title: 'Performance Mode',
      content: 'Toggle performance mode for a clean, focused view during play.',
      position: TooltipRelativePosition.RIGHT,
    },
    {
      targetId: 'rep-tempo-container',
      title: 'Tempo Controls',
      content:
        'Open the control panel to adjust BPM with the slider or +/- buttons, reset to goal tempo.',
      position: TooltipRelativePosition.RIGHT,
      preStepFunction: () => {
        setShowDetails(true);
      },
      postStepFunction: () => {
        setShowDetails(false);
      },
    },
    {
      targetId: 'rep-accuracy-dropdown',
      title: 'Accuracy Feedback',
      content: 'Toggle accuracy feedback on/off for each hand.',
      position: TooltipRelativePosition.LEFT,
      preStepFunction: () => {
        setShowDetails(false);
        setShowAccuracyDetails(true);
      },
      postStepFunction: () => {
        setShowAccuracyDetails(false);
      },
    },
  ];

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: 'white' }}
    >
      {/* {native && inPerformanceMode && !headerVisible && (
        <>
          <TopRevealZone onReveal={() => showHeaderTemporarily()} />
          <FloatingHeaderToggle
            onToggle={() => {
              // manual toggle: if opening, auto-hide in 3s
              if (!headerVisible) showHeaderTemporarily();
              else setHeaderVisible(false);
            }}
          />
        </>
      )} */}

      <Box sx={{ position: 'fixed', width: '100%', zIndex: 2 }}>
        <Header
          repTourOpen={repTourOpen}
          showAccuracy={showAccuracy}
          accuracyPercent={accuracyPercent}
          showCursorSelector={showCursorSelector}
          setShowCursorSelector={setShowCursorSelector}
          headerVisible={headerVisible}
          togglePerformanceMode={togglePerformanceMode}
          inPerformanceMode={inPerformanceMode}
          tempo={tempo}
          performanceTempo={performanceTempo}
          sliderValue={sliderValue}
          setSliderValue={setSliderValue}
          tempPctAdjustment={tempPctAdjustment}
          setTempPctAdjustment={setTempPctAdjustment}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          showAccuracyDetails={showAccuracyDetails}
          setShowAccuracyDetails={setShowAccuracyDetails}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          manualTempoInput={manualTempoInput}
          setManualTempoInput={setManualTempoInput}
          setManualTempoChange={setManualTempoChange}
          mapTempoToSlider={mapTempoToSlider}
          tempoInputRef={tempoInputRef}
          max_tempo_value={max_tempo_value}
          min_tempo_value={min_tempo_value}
          repertoireProgressSelected={repertoireProgressSelected}
          setCursorIsVisible={setCursorIsVisible}
          cursorIsVisible={cursorIsVisible}
          transposeSemitones={transposeSemitones}
          onTransposeChange={handleTransposeChange}
          modeConversion={modeConversion}
          onModeConversionChange={handleModeConversionChange}
          showTransposeControl={isOSMDMode}
        />
      </Box>
      <Box
        className="main-app-container-styles"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1, // fill remaining viewport height (header & footer are position:fixed)
          minHeight: 0, // allow flex child to shrink below content size
          alignContent: 'center',
          alignItems: 'center',
          paddingTop: '0px', //'170px', // since making header fixed position
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        ref={containerRef}
      >
        {/* <Box sx={{position: 'absolute', left: '10px', top: '150px'}}>
          ON: {capMidiReceived.on}
          <br/>
          OFF: {capMidiReceived.off}
          <br/>
          Other: {capMidiReceived.other}
          <br/>
          connected: {`${capMidiReceived.connected}!`}
        </Box> */}
        {/* In OSMD mode, title/composer are rendered inside the Phaser scene
            so they scroll with the music. In SVG mode, keep the React DOM elements. */}
        {!isOSMDMode && (
          <Box
            sx={{
              marginTop: '180px',
              width: '1000px',
              maxWidth: '90%',
              display: 'flex',
              flexDirection: 'column',
              gap: '30px',
            }}
          >
            <Box sx={songTitleStyles}>{repertoireSelected && repertoireSelected.name}</Box>
            <Box sx={composerStyles}>{getNamesConcatted(repertoireSelected.composers)}</Box>
          </Box>
        )}
        {isOSMDMode ? (
          <RepertoirePlayContainerOSMD
            key={`rep-osmd-${transposeSemitones}-${modeConversion}`}
            showKeyboard={false}
            setAccuracy={setAccuracy}
            setShowAccuracy={setShowAccuracy}
            cursorIsVisible={cursorIsVisible}
            transposeSemitones={transposeSemitones}
            modeConversion={modeConversion}
            scrollPosition={scrollPosition}
            setScrollPosition={setScrollPosition}
          />
        ) : (
          <RepertoirePlayContainer
            showKeyboard={false}
            setAccuracy={setAccuracy}
            setShowAccuracy={setShowAccuracy}
            cursorIsVisible={cursorIsVisible}
            scrollPosition={scrollPosition}
            setScrollPosition={setScrollPosition}
          />
        )}
      </Box>
      {/* Bottom-left info button to launch tour */}
      <FooterContainer
        headerVisible={headerVisible}
        playLevel={playLevel}
        softRefreshRepertoire={handleSoftRefresh}
        afterPrimaryAction={() => {
          if (native && inPerformanceMode) {
            if (!repertoireReducerData.repertoirePlaying) {
              hideHeaderSoon(200);
            }
          }
        }}
        onShowInfo={() => setRepTourOpen(true)}
      />
      {/* App tour component */}
      <AppTour
        steps={RepertoireTourSteps}
        isOpen={repTourOpen}
        onClose={() => {
          dispatch(
            appActions.updateUserData({
              repertoire_tutorial_seenat: new Date(),
            }),
          );
          setRepTourOpen(false);
        }}
        onStepChange={() => {}}
        tooltipClassName="tour-tooltip"
        onSkipTour={() => {
          dispatch(
            appActions.updateUserData({
              repertoire_tutorial_seenat: new Date(),
            }),
          );
          setRepTourOpen(false);
        }}
      />
    </Box>
  );
};

const accuracyColors = [
  '#00C2FF', //neutral
  '#40E16D', // green
  '#EBC334', // yellow
  '#ff7d6f',
  // 'rgba(200, 128, 114, 1.0)',// '#EB3459', // red
];

interface HeaderProps {
  accuracyPercent: number | null | undefined;
  showAccuracy: boolean;
  showCursorSelector: boolean;
  setShowCursorSelector: (x: boolean) => void;
  headerVisible: boolean;
  togglePerformanceMode: () => void;
  inPerformanceMode: boolean;
  tempo: number;
  performanceTempo: number | null;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  tempPctAdjustment: number;
  setTempPctAdjustment: (value: number) => void;
  showDetails: boolean;
  setShowDetails: (value: boolean) => void;
  showAccuracyDetails: boolean;
  setShowAccuracyDetails: (value: boolean) => void;
  showSettings: boolean;
  setShowSettings: (value: boolean) => void;
  manualTempoInput: string;
  setManualTempoInput: (value: string) => void;
  setManualTempoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  mapTempoToSlider: (tempo: number) => void;
  tempoInputRef: React.RefObject<HTMLInputElement | null>;
  max_tempo_value: number;
  min_tempo_value: number;
  repertoireProgressSelected: UserRepertoireProgress | undefined;
  cursorIsVisible: boolean;
  repTourOpen: boolean;
  transposeSemitones: number;
  showTransposeControl: boolean;
  onTransposeChange: (nextSemitones: number) => void;
  modeConversion: ParallelModeConversion;
  onModeConversionChange: (nextMode: ParallelModeConversion) => void;
  setCursorIsVisible: (x: boolean) => void;
}

const Header = (props: HeaderProps) => {
  const {
    repTourOpen,
    accuracyPercent,
    headerVisible,
    togglePerformanceMode,
    inPerformanceMode,
    tempo,
    performanceTempo,
    sliderValue,
    setSliderValue,
    tempPctAdjustment,
    setTempPctAdjustment,
    showDetails,
    setShowDetails,
    showAccuracyDetails,
    setShowAccuracyDetails,
    showSettings,
    setShowSettings,
    manualTempoInput,
    setManualTempoInput,
    setManualTempoChange,
    mapTempoToSlider,
    tempoInputRef,
    max_tempo_value,
    min_tempo_value,
    repertoireProgressSelected,
    setCursorIsVisible,
    cursorIsVisible,
    transposeSemitones,
    showTransposeControl,
    onTransposeChange,
    modeConversion,
    onModeConversionChange,
  } = props;

  const tempoContainerRef = useRef<HTMLImageElement>(null);
  const tempoToggleRef = useRef<HTMLDivElement>(null);
  const accuracyContainerRef = useRef<HTMLImageElement>(null);
  const accuracyToggleRef = useRef<HTMLDivElement>(null);
  const settingsContainerRef = useRef<HTMLImageElement>(null);
  const settingsToggleRef = useRef<HTMLDivElement>(null);

  const [_isSmallScreen, setIsSmallScreen] = useState(false); //useState(window.innerWidth <= smallScreenBreakWidth || window.innerHeight <= smallScreenBreakHeight);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(false);
    }; //window.innerWidth <= smallScreenBreakWidth || window.innerHeight <= smallScreenBreakHeight); }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // This function checks if the click is outside the modal
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      const targetElement = target instanceof HTMLElement ? target : null;
      // Ignore clicks on MUI select dropdown surfaces/options so Settings
      // doesn't close before the Select onChange can fire.
      const isSelectMenuInteraction = !!targetElement?.closest(
        '.MuiMenu-paper, .MuiPopover-root, .MuiMenuItem-root, [role="listbox"], [role="option"]',
      );
      if (isSelectMenuInteraction) {
        return;
      }
      if (
        tempoContainerRef.current &&
        !tempoContainerRef.current.contains(target) &&
        !tempoToggleRef.current?.contains(target) &&
        !repTourOpen
      ) {
        setShowDetails(false);
      }
      if (
        accuracyContainerRef.current &&
        !accuracyContainerRef.current.contains(target) &&
        !accuracyToggleRef.current?.contains(target) &&
        !repTourOpen
      ) {
        setShowAccuracyDetails(false);
      }
      if (
        settingsContainerRef.current &&
        !settingsContainerRef.current.contains(target) &&
        !settingsToggleRef.current?.contains(target) &&
        !repTourOpen
      ) {
        setShowSettings(false);
      }
      // Close other dropdowns when opening one
      if (tempoToggleRef.current?.contains(target)) {
        setShowAccuracyDetails(false);
        setShowSettings(false);
      }
      if (accuracyToggleRef.current?.contains(target)) {
        setShowDetails(false);
        setShowSettings(false);
      }
      if (settingsToggleRef.current?.contains(target)) {
        setShowDetails(false);
        setShowAccuracyDetails(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    repTourOpen,
    tempoContainerRef,
    accuracyContainerRef,
    settingsContainerRef,
    setShowDetails,
    setShowAccuracyDetails,
    setShowSettings,
  ]); // Only re-run if detailsRef or setShowDetails changes

  const navigate = useNavigate();

  let accuracyColor;
  if (accuracyPercent == null) {
    accuracyColor = accuracyColors[0];
  } else if (accuracyPercent >= 95) {
    accuracyColor = accuracyColors[1];
  } else if (accuracyPercent >= 75) {
    accuracyColor = accuracyColors[2];
  } else {
    accuracyColor = accuracyColors[3];
  }

  const headerStyle = {
    transform: headerVisible ? 'translateY(0)' : 'translateY(-110px)', // Slide up by full header height
    transition: 'transform 0.5s ease-in-out',
    position: 'fixed',
    width: '100%',
    zIndex: 10, // Ensure header is above other content
    // Other existing styles...
  };

  return (
    <Box sx={{ ...headerStyle, height: '110px', display: 'flex', position: 'relative' }}>
      <Box
        sx={{
          background: '#040B13',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            marginLeft: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Box sx={backcontainerStyles} onClick={() => navigate('/repertoire')}>
              <img style={{ height: '100%' }} src={RepBackArrow} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
            }}
          >
            <Box>
              <Box sx={{ position: 'relative' }}>
                {showDetails && (
                  <TempoContainer
                    tempo={tempo}
                    setShowDetails={setShowDetails}
                    tempoContainerRef={tempoContainerRef}
                    sliderValue={sliderValue}
                    setSliderValue={setSliderValue}
                    tempPctAdjustment={tempPctAdjustment}
                    setTempPctAdjustment={setTempPctAdjustment}
                    performanceTempo={performanceTempo}
                    manualTempoInput={manualTempoInput}
                    setManualTempoInput={setManualTempoInput}
                    setManualTempoChange={setManualTempoChange}
                    mapTempoToSlider={mapTempoToSlider}
                    tempoInputRef={tempoInputRef}
                    max_tempo_value={max_tempo_value}
                    min_tempo_value={min_tempo_value}
                    repertoireProgressSelected={repertoireProgressSelected}
                    cursorIsVisible={cursorIsVisible}
                    setCursorIsVisible={setCursorIsVisible}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '210px',
                  backgroundColor: '#243056',
                  borderRadius: '33px',
                  padding: '3px',
                  marginRight: '15px',
                  '&:hover': {
                    cursor: 'pointer',
                    transition: '0.3s',
                    borderRadius: '33px',
                    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
                  },
                }}
                onClick={() => {
                  setShowDetails(!showDetails);
                }}
                ref={tempoToggleRef}
              >
                <img
                  style={{
                    ...controlsImagesStyles,
                    marginLeft: '1px',
                    minHeight: '56px',
                    marginTop: '1px',
                  }}
                  src={TempoIconLight}
                />
                <Box
                  sx={{
                    ...detailsTitleTextStyles,
                    marginLeft: '10px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#ECECED',
                    fontWeight: 700,
                  }}
                >
                  {tempo} BPM
                  <Box
                    style={{
                      marginLeft: '8px',
                      marginRight: '10px',
                      width: '15px',
                      height: '15px',
                      borderRadius: '50%',
                      backgroundColor: tempo >= (performanceTempo || 0) ? '#40E16D' : '#EB3459',
                    }}
                  />
                  <KeyboardArrowDownIcon style={{ stroke: 'white', width: 28, strokeWidth: 0.8 }} />
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ position: 'relative' }}>
                {showAccuracyDetails && (
                  <AccuracyContainer
                    tempo={tempo}
                    setShowAccuracyDetails={setShowAccuracyDetails}
                    accuracyContainerRef={accuracyContainerRef}
                    sliderValue={sliderValue}
                    setSliderValue={setSliderValue}
                    tempPctAdjustment={tempPctAdjustment}
                    setTempPctAdjustment={setTempPctAdjustment}
                    performanceTempo={performanceTempo}
                  />
                )}
              </Box>
              <Box ref={accuracyToggleRef} sx={controlsStyles}>
                <img
                  style={controlsImagesStyles}
                  src={AccuracyMusicIcon}
                  onClick={() => setShowAccuracyDetails(!showAccuracyDetails)}
                />
              </Box>
            </Box>
            {/* <Box sx={{...controlsStyles, border: showCursorSelector ? '4px solid green' : '4px solid #525969'}}>
            <img 
              style={{...controlsImagesStyles}} 
              src={LoopIcon} 
              onClick={() => setShowCursorSelector(!showCursorSelector)}
            />
            </Box> */}
            <Box
              sx={{
                height: '60px',
                marginLeft: '16px',
                marginRight: '16px',
                borderRight: '2px solid #525969',
              }}
            />
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}
            >
              {/* <Box sx={{...controlsStyles2, boxSizing: 'content-box', border: inPerformanceMode ? '4px solid green' : 'none'}}> */}
              <Box
                sx={{
                  ...controlsStyles2,
                  // boxSizing: 'content-box',
                  // border: inPerformanceMode ? '4px solid green' : 'none'
                  background: inPerformanceMode ? 'rgba(160,255,118,.5)' : 'unset',
                  boxShadow: inPerformanceMode ? '0px 0px 5px 5px rgb(160,255,118,.5)' : 'unset',
                }}
                id="rep-performance-mode"
              >
                <img
                  style={{ maxHeight: '32px' }}
                  src={PerformanceIcon}
                  onClick={() => togglePerformanceMode()}
                />
              </Box>
              <Box sx={{ position: 'relative' }}>
                {showSettings && (
                  <SettingsContainer
                    setShowSettings={setShowSettings}
                    settingsContainerRef={settingsContainerRef}
                    transposeSemitones={transposeSemitones}
                    showTransposeControl={showTransposeControl}
                    onTransposeChange={onTransposeChange}
                    modeConversion={modeConversion}
                    onModeConversionChange={onModeConversionChange}
                  />
                )}
              </Box>
              <Box ref={settingsToggleRef} sx={controlsStyles2}>
                <img
                  style={{ maxHeight: '32px' }}
                  src={SettingsIcon}
                  onClick={() => setShowSettings(!showSettings)}
                />
              </Box>
            </Box>
            <Box
              sx={{
                height: '60px',
                marginLeft: '16px',
                marginRight: '16px',
                borderRight: '2px solid #525969',
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              border: '2px solid #40E16D', //width: '210px',
              height: '50px',
              borderRadius: '30px',
              alignItems: 'center',
              marginRight: '20px',
            }}
          >
            <Box sx={{ ...accuracyStyles, marginLeft: '18px' }}>ACCURACY</Box>
            {/* showAccuracy ? */}
            <Box
              sx={{
                ...accuracyStyles,
                width: '90px',
                marginLeft: '8px',
                marginBottom: '1px',
                marginRight: '18px',
                fontSize: '22px',
                color: accuracyColor,
              }}
            >
              {accuracyPercent != null ? `${accuracyPercent?.toFixed(1)}%` : '--'}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface AccuracyContainerProps {
  accuracyContainerRef: React.RefObject<HTMLImageElement | null>;
}

const AccuracyContainer = (props: AccuracyContainerProps) => {
  const { accuracyContainerRef } = props;

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { errorRecognitionActive } = data;
  const dispatch = useDispatch();

  const topPos = 13; //getPosition().y
  const leftPos = 8; // getPosition().x

  const modalPosition = {
    left: `${leftPos - 32}px`,
    top: `${topPos + 80}px`, // Increased to match settings dropdown gap
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 10,
        ...modalPosition,
        minWidth: '350px',
        borderRadius: '8px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
        padding: '16px',
        backgroundColor: '#050B13',
        animation: 'fadeInSlideDown 0.2s ease-out',
        '@keyframes fadeInSlideDown': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-10px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
      ref={accuracyContainerRef}
      id="rep-accuracy-dropdown"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px', // Half of the carot size to move it above the modal
          left: '40px', // Align this with the hamburger icon
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13', // Carot color should match modal background
        }}
      />
      <Box sx={detailsTitleTextStyles}>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            fontSize: '20px',
            position: 'relative',
          }}
        >
          ACCURACY
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '32px',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              fontSize: '18px',
              color: '#CED0D4',
              fontWeight: 'bold',
            }}
          >
            Show Feedback
          </Box>
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '30px',
            justifyContent: 'center',
            display: 'flex',
            gap: '12px',
          }}
        >
          <Box
            sx={{ '&:hover': { cursor: 'pointer' } }}
            onClick={() =>
              dispatch(
                appActions.setErrorRecognitionActive({
                  ...errorRecognitionActive,
                  left: !errorRecognitionActive.left,
                }),
              )
            }
          >
            <img
              src={errorRecognitionActive.left ? HandActive : HandInactive}
              style={{
                maxHeight: '76px',
                transform: errorRecognitionActive.left ? 'none' : 'scaleX(-1)',
              }}
            />
          </Box>
          <Box
            sx={{ '&:hover': { cursor: 'pointer' } }}
            onClick={() =>
              dispatch(
                appActions.setErrorRecognitionActive({
                  ...errorRecognitionActive,
                  right: !errorRecognitionActive.right,
                }),
              )
            }
          >
            <img
              src={errorRecognitionActive.right ? HandActive : HandInactive}
              style={{
                maxHeight: '76px',
                transform: errorRecognitionActive.right ? 'scaleX(-1)' : 'none',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', padding: '20px 14px', margin: '2px 0px' }}></Box>
      </Box>
    </Box>
  );
};

interface TempoContainerProps {
  setShowDetails: (value: boolean) => void;
  tempo: number;
  performanceTempo: number | null;
  tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  tempPctAdjustment: number;
  setTempPctAdjustment: (value: number) => void;
  manualTempoInput: string;
  setManualTempoInput: (value: string) => void;
  setManualTempoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  mapTempoToSlider: (tempo: number) => void;
  tempoInputRef: React.RefObject<HTMLInputElement | null>;
  max_tempo_value: number;
  min_tempo_value: number;
  repertoireProgressSelected: UserRepertoireProgress | undefined;
}

const TempoContainer = (props: TempoContainerProps) => {
  const {
    setShowDetails: _setShowDetails,
    tempo,
    performanceTempo,
    tempoContainerRef,
    sliderValue,
    setSliderValue,
    tempPctAdjustment,
    setTempPctAdjustment,
    manualTempoInput,
    setManualTempoInput,
    setManualTempoChange,
    mapTempoToSlider,
    tempoInputRef,
    max_tempo_value,
    min_tempo_value,
    repertoireProgressSelected,
  } = props;
  const dispatch = useDispatch();

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const _rep = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const [_displayCursor, _setDisplayCursor] = useState(true);
  const { userData: _userData } = data;

  const topPos = 13; //getPosition().y
  const leftPos = 8; // getPosition().x

  const modalPosition = {
    left: `${leftPos - 32}px`, // Adjust as needed
    top: `${topPos + 80}px`, // Increased to match settings dropdown gap (tempo button is taller than settings icon)
  };

  // Press-and-hold behavior for tempo +/- buttons
  // Note: For RepertoirePlay, updates happen on each tick via mapTempoToSlider
  // (triggers tempo state chain → updateRepertoireDataLocal which is just local Redux state)
  // The main batching benefit is in Lesson.tsx which directly dispatches setTempo
  const { incrementHandlers, decrementHandlers } = useHoldToRepeat(
    tempo,
    mapTempoToSlider, // Called on release (and during hold via onLocalChange)
    {
      min: min_tempo_value,
      max: max_tempo_value,
      onLocalChange: mapTempoToSlider, // Update slider/percentage during hold
    },
  );

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    let actualValue;
    if (Array.isArray(newValue)) {
      newValue = newValue[0];
    }
    if (newValue <= 50) {
      actualValue = 10 + 90 * (newValue / (100 / 2));
    } else {
      actualValue = 100 + 2 * 100 * ((newValue - 100 / 2) / (100 / 2)); // Linearly map to 100-300
    }
    setSliderValue(newValue);
    setTempPctAdjustment(Math.round(actualValue));
  };

  const resetTemp = () => {
    dispatch(appActions.setTempo({ tempo: performanceTempo }));
    setTempPctAdjustment(100);
    setSliderValue(50);
  };

  const handleManualTempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualTempoInput(value);
  };

  useEffect(() => {
    setManualTempoInput(tempo.toString());
    if (repertoireProgressSelected) {
      dispatch(
        repertoireActions.updateRepertoireDataLocal({
          id: repertoireProgressSelected.id,
          last_tempo: tempo,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tempo]);

  return (
    <Box
      id="rep-tempo-container"
      sx={{
        position: 'absolute',
        zIndex: 10,
        ...modalPosition,
        minWidth: '350px',
        borderRadius: '8px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        padding: '16px',
        backgroundColor: '#050B13',
        animation: 'fadeInSlideDown 0.2s ease-out',
        '@keyframes fadeInSlideDown': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-10px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
      ref={tempoContainerRef}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px', // Half of the carot size to move it above the modal
          left: '40px', // Align this with the hamburger icon
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13', // Carot color should match modal background
        }}
      />

      <Box sx={detailsTitleTextStyles}>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            fontSize: '20px',
            position: 'relative',
          }}
        >
          TEMPO
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '30px',
            marginBottom: '16px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              display: 'inline-block',
              '&:hover': { cursor: 'pointer' },
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              touchAction: 'manipulation',
            }}
            {...decrementHandlers}
          >
            <img
              style={{ maxHeight: '28px', verticalAlign: 'top', pointerEvents: 'none' }}
              src={TempoMinus}
              draggable={false}
            />
          </Box>
          <Box sx={{ margin: '0px 20px', display: 'flex', fontSize: '24px', alignItems: 'start' }}>
            <Box sx={{ marginTop: '-3px', marginRight: '1px' }}>
              {/* {tempo} */}
              <TextField
                inputRef={tempoInputRef}
                type="number"
                // value={tempo}
                value={manualTempoInput}
                onChange={handleManualTempoChange}
                onBlur={setManualTempoChange}
                inputProps={{
                  // min: 0,//10,
                  // max: 9999999,//300,
                  style: {
                    textAlign: 'left',
                    color: '#FFFFFF',
                    fontFamily: 'Lato',
                    fontWeight: 400,
                    fontSize: '24px',
                    lineHeight: '24px',
                    letterSpacing: '1px',
                    height: '0px',
                    margin: 0,
                    paddingLeft: '3px',
                    paddingRight: '3px',
                  },
                }}
                sx={{
                  width: '80px',
                  textAlign: 'center',
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                    {
                      '-webkit-appearance': 'none',
                      margin: 0,
                    },
                  '& input[type=number]': {
                    '-moz-appearance': 'textfield',
                  },
                  maxWidth: '54px',
                  marginLeft: 0,
                  padding: 0,
                }}
              />
            </Box>{' '}
            <Box sx={{ fontSize: '16px' }}>BPM</Box>
          </Box>
          <Box
            sx={{
              display: 'inline-block',
              '&:hover': { cursor: 'pointer' },
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              touchAction: 'manipulation',
            }}
            {...incrementHandlers}
          >
            <img
              style={{ maxHeight: '28px', verticalAlign: 'top', pointerEvents: 'none' }}
              src={TempoPlus}
              draggable={false}
            />
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '32px',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {tempo >= performanceTempo ? (
            <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Box
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  backgroundColor: '#40E16D',
                }}
              ></Box>{' '}
              At Tempo
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Box
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  backgroundColor: '#EB3459',
                }}
              ></Box>{' '}
              Below Tempo
            </Box>
          )}
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <StyledSlider
            handleSliderChange={handleSliderChange}
            performanceTempo={performanceTempo}
            sliderValue={sliderValue}
            setSliderValue={setSliderValue}
            tempPctAdjustment={tempPctAdjustment}
            setTempPctAdjustment={setTempPctAdjustment}
          />
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '30px',
            marginBottom: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0px',
          }}
        >
          <Box
            sx={{ magin: 0, padding: 0, '&:hover': { cursor: 'pointer' } }}
            onClick={resetTemp}
            id="rep-reset-button"
          >
            <img style={{ width: '32px' }} src={Reset} />
          </Box>
          Reset
        </Box>
      </Box>
    </Box>
  );
};

interface SettingsContainerProps {
  setShowSettings: (value: boolean) => void;
  settingsContainerRef: React.RefObject<HTMLImageElement | null>;
  transposeSemitones: number;
  showTransposeControl: boolean;
  onTransposeChange: (nextSemitones: number) => void;
  modeConversion: ParallelModeConversion;
  onModeConversionChange: (nextMode: ParallelModeConversion) => void;
}

const SettingsContainer = (props: SettingsContainerProps) => {
  const {
    setShowSettings: _setShowSettings,
    settingsContainerRef,
    transposeSemitones,
    showTransposeControl,
    onTransposeChange,
    modeConversion,
    onModeConversionChange,
  } = props;
  const dispatch = useDispatch();

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const rep = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const { userData } = data;

  const topPos = 13;
  const leftPos = 8;

  const modalPosition = {
    left: `${leftPos - 32}px`,
    top: `${topPos + 48}px`, // Moved up by 4px total
  };

  return (
    <Box
      id="rep-settings-container"
      sx={{
        position: 'absolute',
        zIndex: 10,
        ...modalPosition,
        minWidth: '350px',
        borderRadius: '8px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        padding: '16px',
        backgroundColor: '#050B13',
        animation: 'fadeInSlideDown 0.2s ease-out',
        '@keyframes fadeInSlideDown': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-10px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
      ref={settingsContainerRef}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px',
          left: '50px', // Adjusted to center on the cog icon
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13',
        }}
      />

      <Box sx={detailsTitleTextStyles}>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            fontSize: '20px',
            position: 'relative',
          }}
        >
          SETTINGS
        </Box>

        <Box
          sx={{
            textAlign: 'left',
            marginTop: '26px',
            marginBottom: '16px',
            position: 'relative',
            marginRight: 'auto',
            marginLeft: '60px',
          }}
        >
          <CustomSwitch
            checked={userData?.audio_on ?? true}
            onChange={() => {
              dispatch(appActions.setAudioOn({ audioOn: !(userData?.audio_on ?? true) }));
            }}
          />
          Keyboard Sound
        </Box>

        <Box
          sx={{
            textAlign: 'left',
            marginTop: '6px',
            marginBottom: '16px',
            position: 'relative',
            marginRight: 'auto',
            marginLeft: '60px',
          }}
          id="rep-metronome-cursor-container"
        >
          <CustomSwitch
            checked={userData?.rep_metronome_sound ?? true}
            onChange={() => {
              dispatch(appActions.setRepMetronomeSound(!(userData?.rep_metronome_sound ?? true)));
            }}
          />
          Metronome Sound
        </Box>

        <Box
          sx={{
            textAlign: 'left',
            marginTop: '6px',
            marginBottom: '16px',
            position: 'relative',
            marginRight: 'auto',
            marginLeft: '60px',
          }}
        >
          <CustomSwitch
            checked={userData?.rep_downbeats_sound ?? true}
            onChange={() => {
              dispatch(appActions.setRepDownbeatsSound(!(userData?.rep_downbeats_sound ?? true)));
            }}
          />
          Accent Downbeats
        </Box>

        <Box
          sx={{
            textAlign: 'left',
            marginTop: '6px',
            marginBottom: '16px',
            position: 'relative',
            marginRight: 'auto',
            marginLeft: '60px',
          }}
        >
          <CustomSwitch
            checked={rep.cursorVisible}
            onChange={() => {
              dispatch(repertoireActions.setCursorVisible(!rep.cursorVisible));
            }}
          />
          Cursor
        </Box>
        {showTransposeControl && (
          <Box
            sx={{
              textAlign: 'left',
              marginTop: '6px',
              marginBottom: '16px',
              position: 'relative',
              marginRight: 'auto',
              marginLeft: '60px',
              minWidth: '230px',
            }}
          >
            <Box sx={{ marginBottom: '8px' }}>Transpose</Box>
            <TextField
              select
              size="small"
              value={`${transposeSemitones}`}
              onChange={(event) => {
                onTransposeChange(Number(event.target.value));
              }}
              SelectProps={{
                MenuProps: repertoireSettingsSelectMenuProps,
              }}
              sx={repertoireSettingsSelectSx}
            >
              {TRANSPOSE_SEMITONE_OPTIONS.map((step) => (
                <MenuItem key={step} value={`${step}`}>
                  {getTransposeLabel(step)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {showTransposeControl && (
          <Box
            sx={{
              textAlign: 'left',
              marginTop: '6px',
              marginBottom: '16px',
              position: 'relative',
              marginRight: 'auto',
              marginLeft: '60px',
              minWidth: '230px',
            }}
          >
            <Box sx={{ marginBottom: '8px' }}>Mode</Box>
            <TextField
              select
              size="small"
              value={modeConversion}
              onChange={(event) => {
                onModeConversionChange(event.target.value as ParallelModeConversion);
              }}
              SelectProps={{
                MenuProps: repertoireSettingsSelectMenuProps,
              }}
              sx={repertoireSettingsSelectSx}
            >
              {MODE_CONVERSION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const controlsStyles = {
  height: '54px',
  border: '4px solid #525969',
  borderRadius: '50%',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};
const controlsStyles2 = {
  // height: '54px',
  // border: '4px solid #525969',
  borderRadius: '50%',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};
const controlsImagesStyles = {
  marginTop: '-1px',
  maxHeight: '54px',
};

const accuracyStyles = {
  color: '#ECECEC',
  fontFamily: 'Lato',
  fontSize: '17px',
  fontWeight: '700',
  lineHeight: '20px',
  letterSpacing: '1px',
  textAlign: 'left',
};

const songTitleStyles = {
  fontFamily: 'Lato',
  fontSize: '44px',
  fontWeight: '700',
  lineHeight: '20px',
  letterSpacing: '1px',
  textAlign: 'center', //'left',
};
const composerStyles = {
  fontFamily: 'Lato',
  fontSize: '17px',
  fontWeight: '700',
  lineHeight: '20px',
  letterSpacing: '1px',
  textAlign: 'right', //'left',
  maxWidth: '250px',
  textWrap: 'wrap',
  marginLeft: 'auto',
  marginRight: '20px',
};

const backcontainerStyles = {
  marginRight: '18px',
  height: '25px',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};

type FooterContainerProps = {
  headerVisible: boolean;
  playLevel: () => void;
  softRefreshRepertoire: () => Promise<void>;
  afterPrimaryAction?: () => void;
  onShowInfo: () => void; // NEW: open repertoire tour
};

const FooterContainer = (props: FooterContainerProps) => {
  const { headerVisible, playLevel, softRefreshRepertoire, afterPrimaryAction, onShowInfo } = props;
  const _data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const { repertoireEnded } = repertoireReducerData;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '0px',
        left: '0px',
        width: '100vw',
        display: 'flex',
        height: '100px',
        minHeight: '100px',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(255, 0, 255, 0)',
        transform: headerVisible ? 'translateY(0)' : 'translateY(140px)', // Slide up by full header height
        transition: 'transform 0.5s ease-in-out',
      }}
    >
      <Box
        sx={{
          zIndex: 1,
          position: 'absolute',
          top: 34,
          left: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <IconButton
          id="repertoire-info-button"
          aria-label="Repertoire info"
          onClick={() => onShowInfo()}
          sx={{
            color: '#ECECEC',
            background: 'rgba(36,48,86,0.9)',
            border: '1px solid #525969',
            '&:hover': { background: 'rgba(36,48,86,1.0)' },
            width: 36,
            height: 36,
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Fullscreen show_label={false} use_dark_icon={true} />
      </Box>
      {!repertoireEnded && (
        <Box
          onClick={() => {
            softRefreshRepertoire();
          }}
        >
          <Box sx={restartButtonStyles}>
            <img style={{ maxWidth: '100%' }} src={Restart} />
          </Box>
        </Box>
      )}
      <Box
        onClick={() => {
          playLevel();
          afterPrimaryAction?.();
        }}
      >
        <Box sx={playButtonStyles} id="rep-play-button">
          {repertoireReducerData.repertoirePlaying ? (
            <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={Pause} />
          ) : (
            <>
              {repertoireEnded ? (
                <img
                  style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }}
                  src={RestartBig}
                />
              ) : (
                <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={Play} />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const playButtonStyles = {
  position: 'absolute',
  right: '40px',
  bottom: '30px',
  width: '105px',
  height: '105px',
  padding: '0px',
  margin: '0px',
  borderRadius: '50%',
  '&:hover': {
    background: 'rgba(212,134,108,1.0)',
    cursor: 'pointer',
    transition: '0.3s',
    boxShadow: '0px 0px 5px 5px rgb(212,134,108,.5)',
  },
};

const restartButtonStyles = {
  position: 'absolute',
  right: '160px',
  bottom: '43px',
  width: '75px',
  height: '75px',
  padding: '0px',
  margin: '0px',
  borderRadius: '50%',
  '&:hover': {
    // background: 'rgba(212,134,108,1.0)',
    cursor: 'pointer',
    transition: '0.3s',
    boxShadow: '0px 0px 5px 5px rgb(196,198,202,.5)',
  },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in commented JSX below
const TopRevealZone: React.FC<{ onReveal: () => void }> = ({ onReveal }) => (
  <Box
    onClick={onReveal}
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: 'max(28px, env(safe-area-inset-top))',
      zIndex: 3,
      background: 'transparent',
    }}
  />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in commented JSX above
const FloatingHeaderToggle: React.FC<{ onToggle: () => void }> = ({ onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      position: 'fixed',
      right: 12,
      top: 'calc(env(safe-area-inset-top) + 12px)',
      zIndex: 3,
      background: 'rgba(36,48,86,0.9)',
      borderRadius: '20px',
      padding: '8px 10px',
      border: '1px solid #525969',
      '&:active': { transform: 'scale(0.98)' },
    }}
  >
    <KeyboardArrowDownIcon style={{ color: '#ECECEC' }} />
  </Box>
);

export default RepertoirePlay;

const detailsTitleTextStyles = {
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '15px',
  fontHeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'left',
};
